// ═══════════════════════════════════════════════════════════════
// frontend/src/pages/SaleOrderList.tsx
// ═══════════════════════════════════════════════════════════════

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SaleApi } from "../lib/api";
import { Link } from "react-router-dom";

const stateColors: Record<string, string> = {
  draft: "bg-gray-100 text-gray-700",
  sent: "bg-blue-100 text-blue-700",
  confirmed: "bg-green-100 text-green-700",
  done: "bg-teal-100 text-teal-700",
  cancel: "bg-red-100 text-red-700",
};

export default function SaleOrderList() {
  const queryClient = useQueryClient();
  const { data: orders, isLoading } = useQuery({ queryKey: ["orders"], queryFn: () => SaleApi.list() });

  const confirmMutation = useMutation({
    mutationFn: SaleApi.confirm,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["orders"] }),
  });
  const invoiceMutation = useMutation({
    mutationFn: SaleApi.createInvoice,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["orders"] }); },
  });
  const deliveryMutation = useMutation({
    mutationFn: SaleApi.createDelivery,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["orders"] }); },
  });
  const cancelMutation = useMutation({
    mutationFn: SaleApi.cancel,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["orders"] }),
  });

  if (isLoading) return <p className="text-gray-500">Loading orders...</p>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Sales Orders</h1>
        <Link to="/orders/new" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          + New Order
        </Link>
      </div>
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Order #</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Partner</th>
              <th className="text-center px-4 py-3 font-medium text-gray-600">Status</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Total</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {orders?.map((o: any) => (
              <tr key={o.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium">{o.name}</td>
                <td className="px-4 py-3 text-gray-600">{o.partner_id ?? "—"}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${stateColors[o.state] ?? ""}`}>
                    {o.state}
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-medium">${o.amount_total?.toFixed(2)}</td>
                <td className="px-4 py-3 text-right space-x-2">
                  {o.state === "draft" && (
                    <>
                      <button onClick={() => confirmMutation.mutate(o.id)}
                        className="text-green-600 hover:text-green-800 text-xs font-medium">
                        Confirm
                      </button>
                      <button onClick={() => cancelMutation.mutate(o.id)}
                        className="text-red-600 hover:text-red-800 text-xs font-medium">
                        Cancel
                      </button>
                    </>
                  )}
                  {o.state === "confirmed" && (
                    <>
                      <button onClick={() => invoiceMutation.mutate(o.id)}
                        className="text-blue-600 hover:text-blue-800 text-xs font-medium">
                        Invoice
                      </button>
                      <button onClick={() => deliveryMutation.mutate(o.id)}
                        className="text-orange-600 hover:text-orange-800 text-xs font-medium">
                        Deliver
                      </button>
                      <button onClick={() => cancelMutation.mutate(o.id)}
                        className="text-red-600 hover:text-red-800 text-xs font-medium">
                        Cancel
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {(!orders || orders.length === 0) && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">No orders found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
